import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usePage } from '../../hooks/usePage';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import {
  complianceApi,
  CaseDisposition,
  CaseSeverity,
  CaseStats,
  CaseStatus,
  ComplianceCase,
} from '../../api/compliance';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Table } from '../../components/ui/Table';
import { Drawer } from '../../components/ui/Drawer';
import { Pagination } from '../../components/ui/Pagination';

const PAGE_SIZE = 25;

const SEVERITY_VARIANT: Record<CaseSeverity, 'danger' | 'warning' | 'info' | 'neutral'> = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
  low: 'neutral',
};

const CASE_TYPE_LABEL: Record<string, string> = {
  sanctions_hit: 'Sanctions hit',
  monitoring_alert: 'Monitoring alert',
  ctr: 'Currency report',
  str: 'Suspicious report',
  edd_review: 'Enhanced due diligence',
  periodic_review: 'Periodic review',
};

const DISPOSITIONS: { value: CaseDisposition; label: string; hint: string }[] = [
  { value: 'false_positive', label: 'False positive', hint: 'The match or alert was not the subject.' },
  { value: 'no_action', label: 'No action', hint: 'Reviewed; the activity is explained and legitimate.' },
  { value: 'edd_applied', label: 'EDD applied', hint: 'Enhanced due diligence carried out; relationship continues.' },
  { value: 'reported', label: 'Reported', hint: 'Escalated to the partner institution as a regulatory report.' },
  { value: 'terminated', label: 'Terminated', hint: 'Relationship ended on AML grounds.' },
];

/** Minimum the backend accepts. Mirrored here for a better error, not as the gate. */
const MIN_REASON = 20;

function timeRemaining(dueAt: string | null): { label: string; breached: boolean } {
  if (!dueAt) return { label: '—', breached: false };
  const ms = new Date(dueAt).getTime() - Date.now();
  if (ms <= 0) {
    const over = Math.abs(ms);
    const h = Math.floor(over / 3_600_000);
    return { label: h < 24 ? `${h}h over` : `${Math.floor(h / 24)}d over`, breached: true };
  }
  const h = Math.floor(ms / 3_600_000);
  return { label: h < 24 ? `${h}h left` : `${Math.floor(h / 24)}d left`, breached: false };
}

/**
 * The compliance case queue — the daily workspace.
 *
 * ## What this screen is for
 *
 * CBN baseline standard #6 asks for case management *with workflow*. The
 * difference between this and the read-only risk feed it replaces is that
 * every case leaves here with a recorded decision: an assignee, a
 * disposition, and a reason someone can reconstruct a year later.
 *
 * ## Ordering and the SLA countdown
 *
 * Sorted server-side by severity then due date, so working top-down hits the
 * most urgent unbreached case first. Time remaining is rendered prominently
 * because a queue that does not surface it will miss deadlines — and for an
 * STR the statutory clock is 24 hours.
 *
 * ## Segregation of duties
 *
 * The close action is hidden for a case the current admin raised (policy
 * §16). The backend refuses it regardless; hiding it here is so nobody is
 * offered a button that will be rejected, which is how people learn to work
 * around a control.
 *
 * ## Nothing here is customer-visible
 *
 * Case summaries and dispositions are internal. Disclosing to a customer that
 * they are the subject of a case is an offence under MLPPA s.14.
 */
export const CaseQueue: React.FC = () => {
  usePage('Compliance Cases');
  const { admin, can } = useAuth();
  const toast = useToast();

  const [cases, setCases] = useState<ComplianceCase[]>([]);
  const [stats, setStats] = useState<CaseStats | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<CaseStatus | ''>('open');
  const [breachedOnly, setBreachedOnly] = useState(false);

  const [selected, setSelected] = useState<ComplianceCase | null>(null);
  const [disposition, setDisposition] = useState<CaseDisposition | ''>('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [result, s] = await Promise.all([
        complianceApi.listCases({
          status: statusFilter ? [statusFilter] : undefined,
          breachedOnly,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        }),
        complianceApi.caseStats(),
      ]);
      setCases(result.items);
      setTotal(result.total);
      setStats(s);
    } catch {
      toast.error('Could not load the case queue.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, breachedOnly, page, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // Policy §16: the raiser is not the closer. 'system' raises most cases, so
  // those are closable by anyone with the permission.
  const canClose = useMemo(() => {
    if (!selected || !can('compliance.close')) return false;
    return selected.raisedBy !== admin?.id;
  }, [selected, can, admin]);

  async function close() {
    if (!selected || !disposition || reason.trim().length < MIN_REASON) return;
    setBusy(true);
    try {
      await complianceApi.closeCase(selected.id, disposition, reason.trim());
      toast.success(`Case ${selected.reference} closed.`);
      setSelected(null);
      setDisposition('');
      setReason('');
      void load();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Could not close the case.';
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  async function assignToMe() {
    if (!selected || !admin?.id) return;
    setBusy(true);
    try {
      const updated = await complianceApi.assignCase(selected.id, admin.id);
      setSelected(updated);
      toast.success('Assigned to you.');
      void load();
    } catch {
      toast.error('Could not assign the case.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Card><div className="text-xs text-gray-500">Open</div><div className="text-2xl font-semibold">{stats.open}</div></Card>
          <Card><div className="text-xs text-gray-500">Unassigned</div><div className="text-2xl font-semibold">{stats.unassigned}</div></Card>
          {/* The number an examiner asks for. */}
          <Card>
            <div className="text-xs text-gray-500">Past SLA</div>
            <div className={`text-2xl font-semibold ${stats.breached > 0 ? 'text-red-600' : ''}`}>
              {stats.breached}
            </div>
          </Card>
          <Card>
            <div className="text-xs text-gray-500">Critical</div>
            <div className="text-2xl font-semibold">{stats.bySeverity.critical ?? 0}</div>
          </Card>
        </div>
      )}

      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <select
            className="rounded border px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as CaseStatus | ''); setPage(1); }}
          >
            <option value="open">Open</option>
            <option value="in_review">In review</option>
            <option value="escalated">Escalated</option>
            <option value="closed">Closed</option>
            <option value="">All</option>
          </select>
          <label className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={breachedOnly}
              onChange={(e) => { setBreachedOnly(e.target.checked); setPage(1); }}
            />
            Past SLA only
          </label>
        </div>

        {loading ? (
          <Spinner />
        ) : (
          <>
            <Table
              data={cases}
              emptyMessage="No cases match this filter."
              onRowClick={(row) => setSelected(row)}
              columns={[
                { key: 'reference', header: 'Reference', render: (c) => <span className="font-mono text-xs">{c.reference}</span> },
                { key: 'type', header: 'Type', render: (c) => CASE_TYPE_LABEL[c.caseType] ?? c.caseType },
                { key: 'severity', header: 'Severity', render: (c) => <Badge variant={SEVERITY_VARIANT[c.severity]}>{c.severity}</Badge> },
                { key: 'status', header: 'Status', render: (c) => <Badge variant="neutral">{c.status.replace('_', ' ')}</Badge> },
                {
                  key: 'due',
                  header: 'SLA',
                  render: (c) => {
                    const { label, breached } = timeRemaining(c.dueAt);
                    return <span className={breached ? 'font-semibold text-red-600' : ''}>{label}</span>;
                  },
                },
                { key: 'assignee', header: 'Assignee', render: (c) => (c.assignedTo ? 'Assigned' : <span className="text-gray-400">Unassigned</span>) },
              ]}
            />
            <Pagination
              page={page}
              totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <Drawer isOpen={!!selected} onClose={() => setSelected(null)} title={selected?.reference ?? ''}>
        {selected && (
          <div className="space-y-4 text-sm">
            <div className="flex gap-2">
              <Badge variant={SEVERITY_VARIANT[selected.severity]}>{selected.severity}</Badge>
              <Badge variant="neutral">{selected.status.replace('_', ' ')}</Badge>
              {timeRemaining(selected.dueAt).breached && <Badge variant="danger">Past SLA</Badge>}
            </div>

            <div>
              <div className="text-xs text-gray-500">Summary</div>
              <p className="whitespace-pre-wrap">{selected.summary ?? '—'}</p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-xs text-gray-500">Subject</div>
                <div className="font-mono text-xs">{selected.subjectType}:{selected.subjectId}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Raised by</div>
                <div>{selected.raisedBy}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Due</div>
                <div>{timeRemaining(selected.dueAt).label}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Linked records</div>
                <div>
                  {selected.linkedScreeningIds.length} screening,{' '}
                  {selected.linkedReportIds.length} report(s)
                </div>
              </div>
            </div>

            {selected.status === 'closed' ? (
              <div className="rounded bg-gray-50 p-3">
                <div className="text-xs text-gray-500">Disposition</div>
                <div className="font-medium">{selected.disposition}</div>
                <p className="mt-1 whitespace-pre-wrap">{selected.dispositionReason}</p>
              </div>
            ) : (
              <div className="space-y-3 border-t pt-3">
                {!selected.assignedTo && can('compliance.review') && (
                  <Button disabled={busy} onClick={assignToMe}>Assign to me</Button>
                )}

                {can('compliance.close') && !canClose && (
                  // Policy §16 made visible rather than silently rejected.
                  <p className="rounded bg-amber-50 p-2 text-xs text-amber-800">
                    You raised this case, so you cannot close it. Another
                    reviewer must dispose of it.
                  </p>
                )}

                {canClose && (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold uppercase text-gray-500">Close case</div>
                    <select
                      className="w-full rounded border px-2 py-1"
                      value={disposition}
                      onChange={(e) => setDisposition(e.target.value as CaseDisposition)}
                    >
                      <option value="">Select a disposition…</option>
                      {DISPOSITIONS.map((d) => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </select>
                    {disposition && (
                      <p className="text-xs text-gray-500">
                        {DISPOSITIONS.find((d) => d.value === disposition)?.hint}
                      </p>
                    )}
                    <textarea
                      className="w-full rounded border px-2 py-1"
                      rows={4}
                      placeholder="Why is this the right disposition? This is the record an examiner reads."
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <div className="text-xs text-gray-500">
                      {reason.trim().length}/{MIN_REASON} characters minimum
                    </div>
                    <Button
                      disabled={busy || !disposition || reason.trim().length < MIN_REASON}
                      onClick={close}
                    >
                      {busy ? 'Closing…' : 'Close case'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};
